/**
 * Business rules shared by the alarm engine (server) and the UI (web) so that
 * thresholds never drift between the two.
 */

/** 化验关注阈值（联动救灾模拟的极危线）. */
export const LAB_THRESHOLDS = {
  nh3nWarn: 1.2, // NH₃-N > 1.2 → 关注
  no2Warn: 0.6, // NO₂-N > 0.6 → 关注
  nh3nCritical: 2, // > 2 → 极危（联动救灾模拟）
  no2Critical: 1, // > 1 → 极危
  phMin: 6.5,
  phMax: 8.5,
  /** 化验时效：超过该小时数标黄 */
  staleHours: 24,
} as const;

/** DO control band for hang / RAS units. */
export const DO_BAND = { low: 4.0, high: 6.5 } as const;

/** Allowed work-order status transitions. */
export const WORKORDER_TRANSITIONS: Record<string, string[]> = {
  open: ['assigned', 'in_progress', 'closed'],
  assigned: ['in_progress', 'assigned', 'closed'],
  in_progress: ['resolved', 'assigned', 'closed'],
  resolved: ['closed', 'in_progress'], // in_progress = reopen
  closed: [],
};

export type AlarmRuleKey = 'do_low' | 'o2_pressure' | 'equipment_jam' | 'lab_critical' | 'makeup_low_pressure';

export interface AlarmRule {
  key: AlarmRuleKey;
  level: 'L1' | 'L2' | 'L3';
  system: string;
  message: string;
  source: string;
}

/**
 * Deterministic rule set evaluated by the alarm engine each tick.
 * The engine compares live telemetry against these thresholds; a matching
 * rule raises an alarm (deduped by key+system) and fans out notifications.
 */
export const ALARM_RULES: AlarmRule[] = [
  { key: 'do_low', level: 'L2', system: '吊养', message: 'DO 低于预警阈值', source: 'rule:do_low' },
  { key: 'o2_pressure', level: 'L2', system: '公辅-纯氧', message: '主路纯氧压力异常', source: 'rule:o2_pressure' },
  {
    key: 'equipment_jam',
    level: 'L1',
    system: '补水',
    message: '设备卡涩/执行超时 · READBACK_WATCHDOG 500ms 内限位反馈未达到期望值',
    source: 'FC16 ACK≠物理ACK',
  },
  { key: 'lab_critical', level: 'L1', system: '吊养', message: '化验极危（联动救灾模拟）', source: 'rule:lab_critical' },
  { key: 'makeup_low_pressure', level: 'L2', system: '源水', message: '源水补水低压熔断', source: 'rule:makeup_low_pressure' },
];

/** True if an alarm matches an active (in-window) suppression rule. */
export function isSuppressed(
  alarm: { system: string; source?: string; level: string },
  windows: { system?: string; source?: string; level?: string; from: string; to: string }[],
  now: number,
): boolean {
  return windows.some((w) => {
    if (new Date(w.from).getTime() > now || new Date(w.to).getTime() < now) return false;
    if (w.system && !alarm.system.toLowerCase().includes(w.system.toLowerCase())) return false;
    if (w.source && !(alarm.source ?? '').toLowerCase().includes(w.source.toLowerCase())) return false;
    if (w.level && alarm.level !== w.level) return false;
    return true;
  });
}
